// Word Garden — per-word plant visuals (Stage 2, PR 2).
//
// One colour SVG sprite (image/garden/garden.svg) is injected into the page at
// boot; every surface that shows a word's growth stage renders a plant from it:
//   <svg class="pl-plant …" viewBox="24 2 80 98"><use #g-mound><use #g-plant-K></svg>
// The sprite is INLINED (not referenced as <use href="garden.svg#id">) because an
// external <use> would not clone the shared <defs> gradients the plants paint with
// — same-document references resolve them; cross-document ones do not.
//
// The stage per (user, term) comes from POST api/garden/term-status. A "slot" span
// is emitted wherever a word appears (rich header, lean rows, WL rows, trays); the
// decorate pass fills the planted / graduated ("Ready") ones and leaves the rest
// empty (collapsed by CSS) — "no state → no icon" on dense surfaces.
//
// NB: jQuery 1.9's $("<svg>…") builds elements in the HTML namespace (they never
// render), so all SVG here is written through .html()/innerHTML, letting the
// browser's foreign-content parser create real SVG nodes.

window.GARDEN = (function () {
	"use strict";

	var SPRITE_URL = "/image/garden/garden.svg";
	var HOST_ID = "gardenSpriteHostId";
	var spriteReady = false;
	var deferredHydrate = false;   // a decorate ran before the sprite arrived → re-hydrate on load

	// main.js's escHtml is closure-local, so garden.js carries its own (getRes/strFormat are util.js globals).
	function esc(s) {
		return String(s == null ? "" : s)
			.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
	}

	// Stage names (still en-only literals — i18n keys land later, see word-garden-progress note).
	//
	// Stages 4 and 5 were "Forming" and "Opening" until 2026-08-15. Those came from the ART
	// FILENAMES — the bud forming, the bud opening — so they named the DRAWING while their five
	// neighbours named the reader's memory, and the drawing is already on screen right above the
	// label. Nobody noticed for a month because a tooltip only ever shows one of them; the growth
	// explainer's ladder puts all eight in a row, and "Opening" read as a question (owner, same
	// day). "Taking root" / "Rooted" are the metaphor the panel's own prose already uses — "the
	// waits between waterings stretch as the word takes root" — and unlike the old pair they say
	// something about the WORD rather than about the picture.
	// ⛔ These were hardcoded ENGLISH LITERALS until 2026-09-01 — the only ladder rung wired to
	// i18n was stage 0 below. So `garden_grow_from` rendered «выросло из Rooted» to a Russian
	// reader, and the growth explainer's eight-rung ladder was seven-eighths English in every
	// non-English UI (docs/project/i18n-runtime-grammar.md §7). Keys, not literals, now.
	//
	// Still a literal MAP of key names rather than getRes() calls, for the reason stage 0 gives
	// below: this object is built at parse time and resources_en.js may not have loaded yet
	// (CLAUDE.md gotcha #1). stageDisplayName() is the only place that resolves them, lazily.
	var STAGE_NAME_KEYS = {
		1: "garden_stage1_name", 2: "garden_stage2_name", 3: "garden_stage3_name",
		4: "garden_stage4_name", 5: "garden_stage5_name", 6: "garden_stage6_name",
		7: "garden_stage7_name"
	};

	// Stage 0 — the v2 growth engine's SPROUT (garden-srs-redesign-spec.md Sec 2, R38: "sprout at
	// practice 0"), a brand-new state the v1 stage ladder above never had to name. Unlike
	// 1-7 this one is wired to i18n from day one (getRes(), key applied by the P6 conductor pass —
	// until then getRes() prints the raw key, an accepted inert-window state: the whole surface is
	// flag-gated, so nobody sees it live yet). Called lazily, at render time, never at parse time —
	// STAGE_NAME_KEYS itself stays a plain literal object precisely so it never has to worry about
	// resources_en.js not having loaded yet (CLAUDE.md gotcha #1); this one function is the only
	// place that ever calls getRes() before a plant is actually painted.
	function stageDisplayName(stage) {
		if (stage === 0) { return getRes("garden_sprout_stage_name"); }
		var key = STAGE_NAME_KEYS[stage];
		return key ? getRes(key) : "";
	}

	// ── sprite bootstrap ─────────────────────────────────────────────────────
	function ensureHost() {
		var host = document.getElementById(HOST_ID);
		if (!host) {
			host = document.createElement("div");
			host.id = HOST_ID;
			host.className = "gardenSpriteHost";
			host.setAttribute("aria-hidden", "true");
			document.body.insertBefore(host, document.body.firstChild);
		}
		return host;
	}

	function loadSprite() {
		if (spriteReady) { return; }
		var host = ensureHost();
		// Fetch happens at boot; every garden surface renders only after later user
		// actions, so the sprite is in place well before any plant is asked for.
		$.ajax(SPRITE_URL, { dataType: "text" })
			.done(function (svgText) {
				host.innerHTML = svgText;   // foreign-content parse → real SVG defs/groups
				spriteReady = true;
				if (deferredHydrate) { decorate(document.body); }
			});
		// On failure the slots simply stay empty — decoration is never load-bearing.
	}

	// ── plant / droplet builders ─────────────────────────────────────────────
	// `kind`: a stage number 1..7, the v2 sprout's stage number 0, or "germ"/"ready" (graduated,
	// pre-practice). A "seed" kind existed here but was never produced by any caller; the sprite
	// dropped the seed art on 2026-07-19 and this branch went with it.
	//
	// ⚠️ The two shoots are NOT interchangeable, and the id names do not help: "g-germ-body" is
	// STAGE 0's art (the v2 sprout, spec Sec 2 "sprout at practice 0") while the "germ" KIND is
	// Ready and draws "g-ready-body". They shared one drawing until the owner's 2026-08-25 art
	// pick closed the P6 checkpoint — which is why the finish screen could say "Not planted yet"
	// under a picture of a plant and nothing on screen contradicted it (PR #1003).
	//
	// The split matters because these two are the only kinds that differ by whether a plant EXISTS:
	// 0 has a `user_term_garden` row and a watering date, "germ" has neither.
	function bodyId(kind) {
		return kind === 0 ? "g-germ-body"
			: (kind === "germ" || kind === "ready") ? "g-ready-body"
			: "g-plant-" + kind;
	}

	// The sprite's shared frame is sized for a full stage-7 plant (halo and all), so
	// early stages occupy only its bottom slice and render tiny at row height. Dense
	// single-plant surfaces (stage slots, hover peek) therefore crop the frame to the
	// stage's REAL art bounds, measured once per kind with getBBox() on the injected
	// sprite — self-maintaining when the generated art changes. The growth strip keeps
	// the shared frame: its before → after pair reads as growth precisely because the
	// frame is constant across stages.
	var FULL_VB = "24 2 80 98";
	var VB_PAD = 4;          // top/left/right: getBBox is geometry-only, so this covers stroke
	                          // overhang + drop shadows (leaf blades, stage-7 halo/sparkles).
	var VB_PAD_BOTTOM = 0;    // the mound's only overhang below its own geometry is a faint
	                          // blurred ground-shadow — clipping its last sliver is invisible,
	                          // and every surface that lines a plant up with a text baseline
	                          // needs the mound's OWN foot to BE the crop's bottom edge, not a
	                          // few empty px of padding under it (most visible on short early
	                          // stages, where that padding is a large fraction of the height).
	// Cropping to the art's own bounds normalises EVERY kind to the same rendered height —
	// the row height is fixed (--pl-plant-sm) and the viewBox carries the aspect, so a
	// stage-7 rose and the "Ready" shoot both paint ~22px in a 24px row. For stages 1-7
	// that is the point (it is what keeps a one-leaf sprout legible at 16px). For "Ready"
	// it is wrong: the pre-stage has to read as SMALLER than stage 1, not equal to it.
	// So the crop height has a floor. Short art keeps headroom above it and therefore
	// renders proportionally smaller; every real stage is taller than the floor and is
	// untouched (stage 1, the shortest, crops to ~64).
	var MIN_VB_H = 40;
	var vbCache = {};

	function fitViewBox(kind) {
		if (!spriteReady) { return FULL_VB; }   // pre-sprite paints are re-decorated on load
		if (vbCache[kind]) { return vbCache[kind]; }
		var probe = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		probe.style.cssText = "position:absolute;width:0;height:0;visibility:hidden";
		probe.innerHTML = '<g><use href="#g-mound"></use><use href="#' + bodyId(kind) + '"></use></g>';
		document.body.appendChild(probe);
		var b = null;
		try { b = probe.firstChild.getBBox(); } catch (e) { /* detached/unsupported → full frame */ }
		document.body.removeChild(probe);
		if (!b || !b.width) { return FULL_VB; }
		var y = b.y - VB_PAD, h = b.height + VB_PAD + VB_PAD_BOTTOM;
		// grow the floor UPWARD only — the mound's foot must stay the bottom edge (see VB_PAD_BOTTOM)
		if (h < MIN_VB_H) { y -= (MIN_VB_H - h); h = MIN_VB_H; }
		vbCache[kind] = (b.x - VB_PAD) + " " + y + " " + (b.width + 2 * VB_PAD) + " " + h;
		return vbCache[kind];
	}

	// ── parametric withering (PR 5) ──────────────────────────────────────────
	// An overdue plant is the SAME plant, drying out — never different art (a stage
	// never drops, and the picture must not imply it did). Degree 0..1 is how far past
	// its watering date the word is, RELATIVE TO ITS OWN interval:
	//     degree = overdue / (overdue + interval)
	// so a 1-day sprout is half-dry a day late while a 90-day rose fades over months —
	// the wither is honest about memory, not a uniform guilt timer. Saturates toward 1.
	//
	// Once the FSRS engine is active (learn.srs.mode=active) the payload also carries `r`:
	// the model's PREDICTED RECALL PROBABILITY right now. That is the quantity the ratio
	// above was always approximating, so we prefer it — dryness becomes a readout of the
	// user's own forgetting curve. Mapped linearly across the wither bands: fresh at
	// R >= 0.90, fully wilted at R <= 0.65. Plants with no model state yet (planted before
	// the engine, or mode=off/shadow) send no `r` and keep the ratio.
	var WITHER_FRESH_R = .90, WITHER_WILT_R = .65;

	function witherDegree(st) {
		if (!st || !st.overdue) { return 0; }
		if (typeof st.r === "number") {
			var d = (WITHER_FRESH_R - st.r) / (WITHER_FRESH_R - WITHER_WILT_R);
			return Math.max(0, Math.min(1, d));
		}
		var od = +st.overdueDays || 0;
		if (od <= 0) { return 0; }   // due today: the droplet/ring carries the message alone
		return od / (od + Math.max(1, +st.intervalDays || 1));
	}

	// Degree → the render: a dry tint that deepens linearly (anchored on the design
	// mockup's settled "thirsty"/"withered" values) plus a droop that eases in as d²,
	// so a mildly thirsty plant only pales while a long-forgotten one also slumps.
	// Applied to the plant BODY only — the soil mound never withers.
	function witherRender(d) {
		if (!(d > 0)) { return null; }
		d = Math.min(1, d);
		function r2(x) { return Math.round(x * 100) / 100; }
		return {
			filter: "sepia(" + r2(.78 * d) + ") saturate(" + r2(1 - .52 * d)
				+ ") brightness(" + r2(1 - .10 * d) + ") contrast(" + r2(1 - .04 * d) + ")",
			rot: r2(5 * d * d)   // degrees, clockwise = the lean the art already has
		};
	}

	// No <title> child, deliberately (owner report 2026-08-15). It used to carry the plant's
	// sentence and was defended as the graphic's accessible name — but the <svg> is
	// aria-hidden, so screen readers never saw it and the ONLY thing it ever drew was the OS
	// tooltip box, firing under our own hover peek. Two tooltips on one icon, one of them in
	// browser chrome we do not style. The sentence lives in the peek and on the day badge now.
	function plantSvg(kind, cls, fit, wither) {
		var body = '<use href="#' + bodyId(kind) + '"></use>';
		var w = witherRender(wither);
		if (w) {
			// pivot (64 78) = the stem's foot, per the mockup — the plant tips over its
			// root, it doesn't swing in the air. .pl-plant overflows visibly, so the
			// couple of units the droop shifts art past the fitted viewBox still paint.
			body = '<g transform="rotate(' + w.rot + ' 64 78)" style="filter:' + w.filter + '">'
				+ body + "</g>";
		}
		return '<svg class="pl-plant ' + cls + '" viewBox="' + (fit ? fitViewBox(kind) : FULL_VB)
			+ '" aria-hidden="true">'
			+ '<use href="#g-mound"></use>' + body + "</svg>";
	}

	function dropletSvg() {
		return '<svg class="gardenDrop" viewBox="0 0 24 24" aria-hidden="true"><use href="#g-droplet"></use></svg>';
	}

	// An empty stage slot for a term — the decorate pass fills it by id later.
	//
	// `term` is the word's own text, and passing it is what makes the plant a DOOR: given it,
	// the slot carries data-term and the click delegate below opens that word's statistics.
	// Omit it and the plant stays a picture (word-list rows do — see the delegate's note).
	// `size` names the plant's size on this surface, and there are only TWO:
	//   "sm" — word-list rows AND lean rows (dictionary/search results, Practice lists).
	//   "lg" — the roomy single-term surfaces (rich header, hover peeks, growth-bed peek).
	//          See ROOMY below.
	//
	// TWO spans come back, not one: the plant, then the DAY BADGE that says how overdue the word
	// is (or how long until its next watering). On the compact surfaces CSS binds the pair into one
	// fixed-width column with no divider between the parts — the day count IS the plant's watering
	// state and belongs beside it, not in the row's right-hand corner where ×, ✓ and the learning
	// badge already live. Two SIBLING spans rather than one nested slot on purpose: the base rule
	// `.gardenStageSlot:empty { display: none }` is what collapses an unpainted plant on the roomy
	// surfaces, and a slot that always held two children would never be :empty again.
	//
	// `verbKind` ("VERBS" / "CONJUGATION"), the two verb lists only: this slot's id is a verb LEMMA,
	// but the row it sits in is about what that lemma EXPANDS into — the form-set card, or the
	// conjugation cells. Passing it sends the slot's read to verb-term-status instead, so the plant
	// describes the term the row's + button actually adds. See decorate().
	function slot(termId, size, term, verbKind) {
		if (termId == null) { return ""; }
		return '<span class="gardenStageSlot" data-term-id="' + termId
			+ '" data-plant-size="' + (size || "sm") + '"'
			+ (term ? ' data-term="' + esc(term) + '"' : "")
			+ (verbKind ? ' data-verb-kind="' + esc(verbKind) + '"' : "") + '></span>'
			+ '<span class="gardenDaysSlot" data-plant-size="' + (size || "sm") + '"></span>';
	}

	// The two ACTIONS as flat images, for chrome that labels an action rather than showing a
	// word's state: "Add to Learning" (sprout) / "Add to Practice" (watering can), the Learn
	// recap heading, the finish-screen heading. These are the very files the Learn and Practice
	// tabs use, so the whole app names those two ideas with one picture each — they replaced
	// the seedling / direct-hit emoji, which rendered as whatever glyph the OS happened to ship
	// and had nothing to do with the plant art.
	//
	// Practice wears the WATERING CAN, not the stage-7 bloom it used to: practice is the act of
	// watering what you already planted, so the tab shows the tool you do it with. The ladder's
	// blooms still belong to the words themselves (garden.svg stages), which is why the two
	// ideas no longer share one picture.
	var MARK_FILE = { sprout: "tab-sprout", water: "watering-can-smooth" };

	// TRIAL (2026-08-13, owner's call): the two marks are UNICODE GLYPHS again — 🌱 U+1F331
	// SEEDLING for Learning, 💧 U+1F4A7 DROPLET for Practice — in place of the drawn SVGs.
	// The note above records why the drawings replaced the earlier emoji and that objection
	// still stands (a glyph renders as whatever the OS ships), but this is a different pair:
	// the seedling reads as the same germination shoot the plant art draws, and the droplet
	// is the idea the garden's own thirsty badge already carries.
	//
	// MARK_FILE and both .svg files stay put ON PURPOSE — reverting the trial is this one
	// function plus the `span.gardenMark` blocks that sit beside the <img> rules in
	// views/garden.css, components/tabs.css, components/onboarding.css and views/layout.css.
	var MARK_GLYPH = { sprout: "🌱", water: "💧" };

	function markIcon(which) {
		var glyph = MARK_GLYPH[which] || MARK_GLYPH.sprout;
		// Class names are UNCHANGED, so every existing site keeps its sizing hook; the
		// stylesheets tell the two apart by ELEMENT (span vs img) instead. Decorative — the
		// text beside every mark already says what it means, so the glyph is hidden from SR.
		var cls = "gardenMark" + (which === "water" ? " gardenCanClass" : "");
		return '<span class="' + cls + '" aria-hidden="true">' + glyph + '</span>';
	}

	// Surfaces split into two families, and the "thirsty" cue differs between them:
	//
	//   COMPACT (word-list rows and lean rows, both "sm") — the plant is row-height, so a
	//     droplet badge on it is a smudge that reads as dirt on the screen. Those slots get
	//     a blue ring instead (CSS .gardenThirstyClass): same message, legible at row scale.
	//   ROOMY ("lg" — rich header, hover peek, bed peek) — there IS room, so the droplet
	//     earns its place. It sits top-LEFT: every plant's mass leans right (the bloom is
	//     offset that way), so the left shoulder is the empty corner and the badge never
	//     covers art.
	//
	// The hover peek exists precisely to bridge the two — a compact plant you want to read
	// properly magnifies into the roomy size, droplet and all.
	var ROOMY = { lg: 1 };
	function isRoomy(size) { return !!ROOMY[size]; }

	// ── interval → human "next watering" copy ────────────────────────────────
	// DAYS, always, and never a tilde (owner call 2026-08-15). This used to compress to
	// "~3 weeks" / "~2 months" past the first week. Two things were wrong with that: the
	// schedule is quantized to whole days end to end ([[learning-day-due-quantization]]), so
	// the app knows the number exactly and "~3 weeks" threw it away — and the badge sitting
	// beside every one of these sentences prints that exact number, so the two disagreed on
	// the same screen (a badge reading 22 under a tooltip promising three weeks).
	//
	// The unit word is a resource because Russian inflects it by the count (день / дня /
	// дней); every locale that has not authored the three keys falls back to English's two.
	function dayWord(n) {
		var m10 = n % 10, m100 = n % 100;
		if (m10 === 1 && m100 !== 11) { return getRes("garden_day_one"); }
		if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) { return getRes("garden_day_few"); }
		return getRes("garden_day_many");
	}

	function humanizeDays(days) {
		var n = Math.max(1, Math.round(+days || 0));
		return n + " " + dayWord(n);
	}

	// The coarse form the above used to be, kept for the ONE surface that wants it: the growth
	// guide's ladder, whose rungs are a cumulative horizon ("how far off is the flower?") and
	// not a date. There the exact number is the wrong answer — nobody plans around day 115, and
	// "~4 months" is what the paragraph under the ladder promises in words. A schedule the app
	// actually holds a word to is never printed through this.
	function approxDuration(days) {
		days = +days || 0;
		if (days <= 1) { return "~1 day"; }
		if (days < 7) { return "~" + days + " days"; }
		if (days < 30) { var w = Math.round(days / 7); return "~" + w + (w === 1 ? " week" : " weeks"); }
		var m = Math.round(days / 30);
		return "~" + m + (m === 1 ? " month" : " months");
	}

	// ── the day badge ─────────────────────────────────────────────────────────
	// A PLAIN DAY COUNT, and nothing else — the badge is a round numbered dot (garden.css), the
	// same shape the rich card's sense numbers use, so a unit letter riding inside it would be
	// noise in a space that reads as "a number".
	//
	// It used to compress to 1d / 2w / 5m / 2y with a "now" token below one day. Two problems:
	// "now" answered a question nobody asked (the badge says WHEN, and "now" is a state, not a
	// when), and it appeared on every Growing word due in under half a day — a band whose whole
	// definition is "not due yet". CEILING, not rounding, is what fixes that: a word due in six
	// hours is due in one more day, never in zero. The floor of 1 follows for the same reason;
	// the badge never claims a word is due in no time at all.
	//
	// The OVERDUE side rounds the other way (2026-08-02): it FLOORS, and the first day is BLANK.
	// A ceiling there overstated lateness by up to a whole day — a word ten minutes past its date
	// read "1", the same as one left sitting since yesterday — so the number that is supposed to
	// measure neglect could not tell "just came due" from "a day ignored". Now 0 to under 24 h
	// since the word's own due instant draws the yellow badge with NO number, and the count
	// starts at 1 after a full day. Each side therefore rounds AWAY from the boundary it sits
	// next to, and neither ever claims a day that has not happened.
	//
	// The clock is a rolling day from the word's own due instant, deliberately NOT the 03:00
	// learning day (common/LearningDay) — that boundary answers the different question "did I
	// practise it today". BOTH counts arrive already whole: GardenService.putWateringState applies
	// each side's rounding before they reach the wire, so this is a formatter — it prints the
	// number, and draws 0 as the empty badge.
	//
	// Overdue counts can reach three digits, which is why the badge shape stretches past two
	// (garden.css) instead of the number being compressed to fit a circle.
	function shortDays(days) {
		var n = Math.max(0, Math.round(+days || 0));
		return n > 0 ? String(n) : "";
	}

	// The number one plant's badge shows — "" for a word with no watering date at all, and "" again
	// for a word less than a day overdue (the blank first day above). The countdown side keeps its
	// floor of 1 here rather than in shortDays: a Growing word always has at least one more day to
	// wait, so a blank BLUE badge would be a payload glitch showing through, not a state.
	function badgeDays(st) {
		// st.stage == null (not !st.stage): a v2 sprout's stage is literally 0, which is a real
		// planted state with its own watering date — falsy-0 would wrongly read as "no plant".
		if (!st || st.stage == null) { return ""; }
		return st.overdue ? shortDays(st.overdueDays) : shortDays(Math.max(1, +st.dueDays || 0));
	}

	// The badge's own span, emitted by slot() right after the plant's.
	function daysSlotOf(el) {
		var next = el.nextElementSibling;
		return next && next.className && next.classList.contains("gardenDaysSlot") ? next : null;
	}

	// The sentence the DAY BADGE explains — and only the badge. Owner call 2026-08-15: the
	// watering schedule belongs to the number that counts it down, not to the plant, which is
	// there to say what STAGE the word has reached. Splitting them also fixed the sentence
	// nobody could parse ("Recalled · water again ~3 weeks" read as one claim about the stage);
	// the plant now says "Recalled", the badge says how long until the next watering.
	//
	// Null when there is nothing to promise — no plant, or a parked word, whose badge is not
	// drawn at all (a resting word has no next watering date to count down to).
	function waterSentence(st) {
		if (!st || st.stage == null) { return null; }   // st.stage may legitimately be 0 (v2 sprout)
		if (!st.overdue) { return strFormat(getRes("garden_water_next"), humanizeDays(st.dueDays)); }
		// Under a day late reads "due today", not "overdue" — matching the blank badge it
		// explains. A tooltip claiming lateness over a blank count would contradict it.
		return st.overdueDays >= 1
			? strFormat(getRes("garden_water_overdue"), humanizeDays(st.overdueDays))
			: getRes("garden_water_today");
	}

	// Exactly TWO colours, flat: alert for overdue, calm for upcoming. Deliberately NOT graded by
	// severity — the plant already encodes how far overdue a word is through its parametric wither,
	// and a second encoding of the same quantity fights the first.
	// PR 7/7 of the tooltip engine took the badge's own native `title` away (design §6, #12) and
	// folded its sentence into the plant's peek. That is now half undone, on purpose: the
	// schedule came BACK to the badge (as `data-water-tip`, read by the delegate below — our own
	// tooltip, never an OS one), and what stayed on the plant is the stage name alone. Still one
	// explanation per element, which is what the migration was actually protecting.
	function paintDays(el, st) {
		var slotEl = daysSlotOf(el);
		if (slotEl) { applyDays(slotEl, st); }
	}

	// The painter itself, taking the BADGE span rather than the plant beside it — because the badge
	// comes in two shapes now. slot() emits it as the plant's sibling (paintDays finds it that way);
	// the finish screen emits it alone, carrying its own term id (decorateDays below). One painter
	// either way, so the two shapes can never drift into showing the same state differently.
	function applyDays(slotEl, st) {
		slotEl.classList.remove("gardenDaysOverdueClass", "gardenDaysSoonClass");
		slotEl.removeAttribute("data-water-tip");
		// Only a PLANTED word has a watering date. A Ready shoot and an untouched word both leave
		// the badge part empty — reserved, but empty (see the CSS note on why it still holds width).
		// st.stage == null, not !st.stage: a v2 sprout's stage is 0, a real planted state.
		if (!st || st.stage == null) { slotEl.innerHTML = ""; return; }
		// A difficult word the app is bringing back today needs no special face here: the regime
		// writes its plant's due date while it is in the band (redesign D5'''), so it arrives
		// overdue like anything else and counts up in the same yellow. The droplet badge that stood
		// here on 2026-08-02 existed only to paper over a plant whose date said something else.
		var overdue = !!st.overdue;
		slotEl.classList.add(overdue ? "gardenDaysOverdueClass" : "gardenDaysSoonClass");
		// The badge is emitted even when its text is blank: a word due today wears the yellow dot
		// with no number, which is the whole point of the blank first day — it has to be visibly
		// the SAME badge that will read 1 tomorrow, not the absence of one.
		slotEl.innerHTML = '<span class="gardenDaysBadge">' + esc(badgeDays(st)) + "</span>";
		// …and what the number means, for the delegate below. On the badge because THIS is the
		// control the sentence is about: a blank yellow dot is the one badge state whose meaning
		// cannot be guessed from the number, and it is exactly the state that most needs saying.
		var water = waterSentence(st);
		if (water) { slotEl.setAttribute("data-water-tip", water); }
	}

	// A Conjugation row's plant stands for the WEAKEST of several forms (GardenService.verbTermStatus),
	// and a picture of one form is a poor answer to "how is this verb doing" unless it says how many
	// it speaks for. `formCount` rides on that payload and on no other, so every other surface is a
	// no-op here; 1 is left silent — a form-set row has exactly one card and needs no count.
	function withFormCount(line, st) {
		return (st && st.formCount > 1)
			? line + " · " + strFormat(getRes("garden_forms_growing"), st.formCount)
			: line;
	}

	// ── decorate: fill every stage slot in `scope` by term id ─────────────────
	function paintSlot(el, st) {
		el.classList.remove("gardenThirstyClass");
		el.classList.remove("gardenParkedClass");
		// st.stage == null, not !st.stage: a v2 sprout is planted AT stage 0, which is falsy in JS
		// but very much a real state — treating it as "nothing to paint" would blank the plant out.
		if (!st || (st.stage == null && !st.ready)) { el.innerHTML = ""; paintDays(el, null); return; }
		var size = el.getAttribute("data-plant-size") || "sm";
		var kind = st.stage != null ? st.stage : "germ";
		var name = st.stage != null ? stageDisplayName(st.stage) : getRes("garden_ready_stage_name");
		// §6.6 parked: dormant AT ITS HIGH-WATER STAGE, wither frozen. The plant it earned is
		// exactly the plant it keeps — being set aside is not a demotion and must not draw as one.
		// The freeze needs no arithmetic here: the server sends no `r` and overdue false, so
		// witherDegree already reads 0. What is left is to say so — a dormant tint, and NO day
		// badge, because a resting word has no next watering date to count down to.
		if (st.parked) {
			el.classList.add("gardenParkedClass");
			// The one place a plant's line still carries more than the stage name: a resting word
			// has no badge to move the explanation to, and "why is this one greyed out" is the
			// question its picture raises.
			var restTip = withFormCount(name + " · resting — set aside until you wake it", st);
			el.innerHTML = plantSvg(kind, "pl-plant-" + size, true, 0);
			paintDays(el, null);
			el.setAttribute("data-peek-kind", kind);
			el.setAttribute("data-peek-tip", restTip);
			el.setAttribute("data-peek-overdue", "");
			el.setAttribute("data-peek-wither", "");
			el.setAttribute("data-peek-days", "");
			return;
		}
		var wither = witherDegree(st);
		// The plant says what STAGE the word has reached, and nothing else. Its watering
		// schedule moved to the day badge beside it (waterSentence above) — one fact per
		// control, and the two are read together anyway because they sit in one column.
		var tip = withFormCount(st.stage != null ? name : "Ready to grow", st);
		var html = plantSvg(kind, "pl-plant-" + size, true, wither);
		if (st.overdue) {
			if (isRoomy(size)) { html += dropletSvg(); } else { el.classList.add("gardenThirstyClass"); }
		}
		el.innerHTML = html;
		paintDays(el, st);
		// The hover peek rebuilds the plant at its own size from these — cheaper than
		// re-querying the endpoint, and it survives the slot being re-decorated.
		el.setAttribute("data-peek-kind", kind);
		el.setAttribute("data-peek-tip", tip);
		el.setAttribute("data-peek-overdue", st.overdue ? "1" : "");
		el.setAttribute("data-peek-wither", wither ? String(wither) : "");
		// May legitimately be "" on an overdue plant (the blank first day) — data-peek-overdue is
		// what tells the peek a badge exists at all, so the two are read together there.
		el.setAttribute("data-peek-days", badgeDays(st));
	}

	// ── hover peek: the compact plant again at the ROOMY size ─────────────────
	// Both row families (word-list and lean, both "sm") get it — both are small enough to be
	// worth magnifying. The magnified plant is the SAME size the rich header shows
	// (pl-plant-lg) — one roomy plant size in the app, not one per surface — and being
	// roomy it swaps the row's blue thirsty ring back for the real droplet.
	// One reused element on <body> rather than a node per row: the WL can hold thousands.
	// PR 5/7 of the tooltip engine: the box, its placement, its scroll handling and the
	// delegated hover machine are all PL.tip's `peek` surface now (design §4.1) — this
	// file keeps only what is genuinely peek CONTENT. The chrome class and its shown-state
	// class are unchanged (.gardenPeek / .gardenPeekOnClass), so garden.css and the smoke
	// harnesses that select on them are untouched. A peek is mode-INdependent: it is a
	// magnifier, not an explanation, so it stays outside the "info" group too.
	function peekHtml(slotEl) {
		var kind = slotEl.getAttribute("data-peek-kind");
		var days = slotEl.getAttribute("data-peek-days");
		var overdue = slotEl.getAttribute("data-peek-overdue");
		var tip = slotEl.getAttribute("data-peek-tip");
		// The plant and its badge in ONE positioned wrapper, above the text lines. The badge is
		// absolutely placed and used to hang off the BOX's bottom-right corner, which was fine
		// while the plant was the only thing above the sentence — once the peek grew a second
		// line the badge landed on top of it (2026-08-15). Anchoring it to the plant is also
		// what it means: it is that plant's watering count, not the popup's.
		return '<span class="gardenPeekPlantClass">'
			+ plantSvg(kind, "pl-plant-lg", true,
				+slotEl.getAttribute("data-peek-wither") || 0)
			+ (overdue ? dropletSvg() : "")
			// The magnified plant carries its day badge too — the peek is the compact row read
			// properly, so it must not drop information the row itself was showing. `days` alone
			// cannot decide that any more: an overdue plant under a day late has a badge whose
			// text is empty, and a parked or unplanted slot has no badge at all. Both read "" —
			// so the overdue flag is what separates them.
			+ ((overdue || days) ? '<span class="gardenDaysSlot gardenPeekDaysClass '
				+ (overdue ? "gardenDaysOverdueClass" : "gardenDaysSoonClass")
				+ '"><span class="gardenDaysBadge">' + esc(days) + "</span></span>" : "")
			+ "</span>"
			// …and the plant's line, which since 2026-08-15 is the STAGE NAME alone ("Recalled").
			// The watering half of the old sentence moved to the badge's own tooltip: the badge
			// is drawn right here too, so the peek is still the row read properly — it just no
			// longer answers a question the thing beside it answers. Same line style as the
			// vista bed peek's — the two peeks are one object at two content lengths.
			+ (tip ? '<div class="gardenPeekLineClass">' + esc(tip) + "</div>" : "")
			// Only a plant that IS a door advertises the door (data-term, see slot()). A
			// word-list row's plant is not one, and a hint promising a click there would be a lie.
			+ (slotEl.getAttribute("data-term")
				? '<div class="gardenPeekLineClass gardenPeekHintClass">'
					+ esc(getRes("garden_stats_click_hint")) + "</div>" : "");
	}

	// Delegated, so rows built after boot (every list expand, every result page) are covered
	// without rebinding. BOTH row families get it — a lean row's plant is exactly as small as
	// a word-list row's, so it wants the same magnifier. The engine's ONE tracker replaces
	// the private capture-phase scroll listener this used to carry (a scroll of ANY ancestor
	// — the word panel scrolls, not the window — drops the popup instead of stranding it).
	// In a ready block, not at parse time: this is a classic script, and window.PL is set
	// by the deferred module entry — after parse, before DOMContentLoaded (js/pl/app.js).
	var PEEK_SEL = ".wlWordRowClass .gardenStageSlot, ._entryBottom .gardenStageSlot";
	$(function () { PL.tip.delegate(document, PEEK_SEL, {
		surface: "peek",
		boxClass: "gardenPeek",
		onClass: "gardenPeekOnClass",
		// An unplanted slot has nothing painted, so there is nothing to magnify.
		when: function (el) { return !!el.getAttribute("data-peek-kind"); },
		content: peekHtml,
		// Hugs the plant's TOP-RIGHT corner — the popup should read as growing out of the
		// thing you are pointing at, so the gap is a hairline, not a float. Flips left /
		// below only when the viewport edge leaves no room: PL.tip.place's "corner-tr",
		// the same rule the vista's bed peek uses off a bare point.
		sides: ["corner-tr"], gap: 2, margin: 4
	}); });

	// The ROOMY plant (the rich card's header — the only "lg" slot) gets the same words in a
	// plain tooltip instead of a peek. A peek is a MAGNIFIER, and this plant is already drawn at
	// the size the peek would magnify it to; popping a second identical plant over the first
	// would be chrome pretending to be information. What it needs is what the peek's LINE says —
	// the stage name, and the fact that it is a door — so that is all it shows.
	//
	// This is also where the plant's OS tooltip used to live (plantSvg's <title>, gone since
	// 2026-08-15): the header is the one surface that had no peek to fall back on, so without
	// this the rich card's plant would have lost its explanation rather than changed it.
	$(function () { PL.tip.delegate(document, '.gardenStageSlot[data-plant-size="lg"]', {
		when: function (el) { return !!el.getAttribute("data-peek-tip"); },
		content: function (el) {
			return esc(el.getAttribute("data-peek-tip"))
				+ (el.getAttribute("data-term")
					? '<p/>' + esc(getRes("garden_stats_click_hint")) : "");
		}
	}); });

	// ── the day badge's own tooltip: the watering schedule ────────────────────
	// Every badge on the page, on ONE instance — the peek's argument for delegating applies
	// here word for word (thousands of word-list rows, all of them rebuilt on every filter).
	// Not `surface: "peek"`: this one EXPLAINS a control rather than magnifying it, so it is a
	// mode-aware tip in the "info" group like the rest of the app's explanations.
	// The peek copy of the badge (.gardenPeekDaysClass) is excluded: it carries no
	// data-water-tip, and a tooltip over a tooltip is the shape this whole change removes.
	$(function () { PL.tip.delegate(document, ".gardenDaysSlot[data-water-tip]", {
		content: function (el) { return esc(el.getAttribute("data-water-tip")); }
	}); });

	// ── the plant is a door: click → that word's statistics ───────────────────
	// Owner ask 2026-08-15. The plant answers "where does this word stand"; the whole of that
	// standing — every session it appeared in, every gap, every verdict, the four charts —
	// already exists one card away ([[word-history]]), and until now only the My Progress
	// tiles could open it. So the picture that raises the question becomes the way in.
	//
	// Wired off data-term, which slot() writes only where the caller passed the word's text:
	// the rich entry header and the lean rows (Dictionary / Learn / Practice). NOT word-list
	// rows — a WL row is a selection surface with its own click model (checkbox, bulk bar,
	// per-row actions), and a door in its status column would fire inside it.
	//
	// Delegated on the document for the same reason the peek is: these rows outlive no
	// binding, every list page rebuilds them.
	$(function () {
		$(document).on("click", ".gardenStageSlot[data-term]", function (e) {
			// An unpainted slot is not a control — same gate the peek uses. A word with a
			// plant may still have an empty history (a first-ever miss plants nothing but is
			// logged); the card renders that as an empty history rather than an error.
			if (!this.getAttribute("data-peek-kind")) { return; }
			if (!window.SESSION_HISTORY) { return; }
			e.preventDefault();
			e.stopPropagation();
			SESSION_HISTORY.openWord(+this.getAttribute("data-term-id"),
				this.getAttribute("data-term"), this);
		});
	});

	// Every status this page has already been told, by term id — including the terms that came
	// back with NO plant, stored as null, because "this word has no plant" is an answer worth
	// remembering too.
	//
	// Page-scoped, and that is the whole of its invalidation story: plants belong to a USER, but
	// every path that changes the user (Sign in, Change account, Sign out) is a full navigation to
	// /login or /logout, so the cache dies with the document rather than outliving its owner. If an
	// in-page account switch is ever added, it has to clear this.
	//
	// It exists for re-renders of rows the user has already seen. Decoration is a round trip, and
	// rows are rebuilt far more often than their plants actually change: every page of an endless
	// scroll, every list reopen, and — since the word filter — every letter typed into a filter box.
	// Painting those from an empty slot each time made the plants flash off and back on with each
	// keystroke, which is what the whole two-pass start was meant to avoid on the row's other half.
	var statusCache = {};

	// Two passes, the same shape the finish screen and the Learn start already use:
	//   pass 1 — everything already known paints SYNCHRONOUSLY, before any request goes out, so a
	//            re-render of familiar rows never shows an empty plant column;
	//   pass 2 — the server is asked about ALL of them anyway (not just the unknown ones) and the
	//            slots repaint. That is what keeps the cache honest: a word drilled, planted, parked
	//            or watered since it was last seen corrects itself one round trip later, and
	//            repainting an unchanged plant is invisible.
	function decorate(scope) {
		var $scope = scope && scope.jquery ? scope : $(scope || document.body);
		var $slots = $scope.find(".gardenStageSlot[data-term-id]");
		if ($scope.is && $scope.is(".gardenStageSlot[data-term-id]")) { $slots = $slots.add($scope); }
		if (!$slots.length) { return; }
		if (!spriteReady) { deferredHydrate = true; }

		// A VERB-LIST row asks a different question about the very same id. Its slot carries a verb
		// LEMMA, but the row adds and grows a DERIVED term — the form-set card, or the conjugation
		// cells, each a first-class term with its own plant (GardenController.verbTermStatus). So the
		// slots split by `data-verb-kind` and each family gets its own request; without the split a
		// verb row would keep drawing the lemma's plant, which is a different word's state.
		var plain = [], byKind = {}, kind;
		$slots.each(function () {
			var k = this.getAttribute("data-verb-kind");
			if (k) { (byKind[k] = byKind[k] || []).push(this); } else { plain.push(this); }
		});
		paintGroup(plain, null);
		for (kind in byKind) {
			if (byKind.hasOwnProperty(kind)) { paintGroup(byKind[kind], kind); }
		}
	}

	// One request per family of slots, both passes included. `verbKind` null = the ordinary
	// "how is this word growing" read; "VERBS" / "CONJUGATION" = the verb-row read, which answers
	// about the row's derived terms while still keying its reply by the lemma id the slot carries —
	// so everything downstream (paintSlot, the peek, the day badge) is untouched.
	//
	// The cache is NAMESPACED by that kind for exactly the same reason the requests are separate: one
	// term id means two different things depending on which list it is sitting in, and a shared key
	// would let a Conjugation row's fold leak onto the same verb's row in My List.
	function paintGroup(slots, verbKind) {
		if (!slots.length) { return; }
		var ns = (verbKind || "") + "|";
		var seen = {}, termIds = [], i, id;
		for (i = 0; i < slots.length; i++) {
			id = slots[i].getAttribute("data-term-id");
			if (!id) { continue; }
			if (!seen[id]) { seen[id] = 1; termIds.push(id); }
			if (statusCache.hasOwnProperty(ns + id)) { paintSlot(slots[i], statusCache[ns + id]); }
		}
		if (!termIds.length) { return; }

		// Tomcat refuses a request past 1,000 parameters (a 500, the whole ask lost), and each id is
		// one. So a big set goes out in slices — every id still asked, as pass 2 above wants.
		for (i = 0; i < termIds.length; i += STATUS_SLICE) {
			askStatus(slots, termIds.slice(i, i + STATUS_SLICE), verbKind, ns);
		}
	}

	var STATUS_SLICE = 500;

	function askStatus(slots, termIds, verbKind, ns) {
		var data = { termIds: termIds };
		if (verbKind) {
			data.kind = verbKind;
			// The reading language, read off the same hidden field every word-list call uses — the
			// derived cards are per pair, and one generated for another reading language is not what
			// this row would add.
			data.defLang = $("#langFromValId").attr("data-lang-code");
		}
		$.ajax(verbKind ? "api/garden/verb-term-status" : "api/garden/term-status", {
			type: "POST", data: data, dataType: "json", traditional: true,
			success: function (payload) {
				if (!payload || !payload.terms) { return; }
				var byId = {}, asked = {}, j, id;
				for (j = 0; j < payload.terms.length; j++) { byId[payload.terms[j].termId] = payload.terms[j]; }
				// Cache the misses as null too — an unplanted word asked about again is still
				// unplanted, and without this every such row would keep costing a blank first paint.
				for (j = 0; j < termIds.length; j++) {
					statusCache[ns + termIds[j]] = byId[termIds[j]] || null;
					asked[termIds[j]] = 1;
				}
				// Only this slice's slots: another slice's answer is still on the wire, and painting
				// its slots from this reply would blank their plants until it lands.
				for (j = 0; j < slots.length; j++) {
					id = slots[j].getAttribute("data-term-id");
					if (asked[id]) { paintSlot(slots[j], byId[id]); }
				}
			},
			error: function () { /* decoration — stay quiet */ }
		});
	}

	// ── standalone day badges: the watering countdown with no plant beside it ─
	// The finish screen's words want the number and NOT a plant: "Grew today" already draws each
	// word's stage pair, the bed beside the list is the same plants again at full size, and a third
	// copy per word would be three pictures of one fact. So a bare `.gardenDaysSlot[data-term-id]`
	// is the badge's second shape — same span, same two faces, same tooltip delegate, carrying its
	// own term id instead of reading one off a neighbour.
	//
	// It deliberately does NOT consult statusCache. Its one caller paints the moment a session ends,
	// and the next-watering dates it is printing are exactly what that session just rewrote — the
	// same reason renderGrowthBed re-asks between its two passes instead of reusing pass 1's answer.
	// `onDone`, optional: called once painting has settled (success OR error OR nothing to paint
	// at all) — the ONE hook the v2 soft-words pass (appendGrowthSoftWords) needs, since it must
	// run AFTER these badges exist and never before (main.js sequences it this way rather than
	// guessing at a delay).
	function decorateDays(scope, onDone) {
		var $scope = scope && scope.jquery ? scope : $(scope || document.body);
		var $slots = $scope.find(".gardenDaysSlot[data-term-id]");
		if (!$slots.length) { if (onDone) { onDone(); } return; }

		var seen = {}, termIds = [];
		$slots.each(function () {
			var id = this.getAttribute("data-term-id");
			if (id && !seen[id]) { seen[id] = 1; termIds.push(id); }
		});
		if (!termIds.length) { if (onDone) { onDone(); } return; }

		$.ajax("api/garden/term-status", {
			type: "POST", data: { termIds: termIds }, dataType: "json", traditional: true,
			success: function (data) {
				var byId = {};
				var terms = (data && data.terms) || [];
				for (var i = 0; i < terms.length; i++) { byId[terms[i].termId] = terms[i]; }
				// A word with no plant paints as an empty span, which `.gardenDaysSlot:empty`
				// collapses — so an unplanted word costs no gap in the line it sits in.
				$slots.each(function () { applyDays(this, byId[this.getAttribute("data-term-id")]); });
				if (onDone) { onDone(); }
			},
			error: function () { if (onDone) { onDone(); } /* decoration — stay quiet otherwise */ }
		});
	}

	// ── v2 badge-tooltip addition: the next growth day, in SOFT WORDS ONLY ────────────────────
	// spec Sec 2 badge-tooltip rule (R38): never a date, never a raw count.
	//
	// Five spans since 2026-08-25, up from three (docs/project/finish-screen-classification.md).
	// A RETUNE, not a rule change — the owner asked what needed reversing and the answer was
	// nothing: R38 says "soft words only, never a date", and an approximate SPAN in words is
	// inside that. The "never a raw count" clause was this comment's own tightening on the way
	// into code, and it already invited the retune ("the coarseness itself is the requirement,
	// not these exact cutoffs").
	//
	// What the extra spans buy: the old ladder collapsed everything past a fortnight into "further
	// out", so a word 30 days from its mark and a word 90 days from it read identically — which is
	// what made "The flower is not due to grow yet" look like a repeated non-answer. The reason and
	// the WHEN now sit together. Still words, never digits ("in about a month", never "~30 days"),
	// which is what keeps it on the soft side of R38.
	//
	// Cutoffs are a first cut and owner-tunable; they straddle the growth ladder's own marks
	// (1, 3, 7, 14, 30, 60, 90) rather than landing on them, so a word sitting exactly on a mark
	// does not flip phrase on a one-day drift.
	var SOFT_MARK_SPANS = [
		[3, "garden_nextMark_fewDays"],
		[14, "garden_nextMark_twoWeeks"],
		[45, "garden_nextMark_month"],
		[75, "garden_nextMark_twoMonths"]
	];
	var SOFT_MARK_BEYOND = "garden_nextMark_fewMonths";

	function softNextMarkPhrase(days) {
		if (days == null) { return null; }
		for (var i = 0; i < SOFT_MARK_SPANS.length; i++) {
			if (days <= SOFT_MARK_SPANS[i][0]) { return getRes(SOFT_MARK_SPANS[i][1]); }
		}
		return getRes(SOFT_MARK_BEYOND);
	}

	// Appends the soft-words phrase onto the watering badges' EXISTING tooltip (waterSentence),
	// for exactly the words `growthWordsByTermId` ({termId: nextMarkDays}) names — the finish
	// screen's own v2 data, and the ONLY source this reads (no extra request, no whole-garden v2
	// compute — see docs/project's P6 report for why this stays finish-screen-scoped for now).
	// Call ONLY after decorateDays' own painting has settled (its onDone), or the badges this
	// looks for will not exist yet.
	function appendGrowthSoftWords(scope, growthWordsByTermId) {
		if (!growthWordsByTermId) { return; }
		var $scope = scope && scope.jquery ? scope : $(scope || document.body);
		$scope.find(".gardenDaysSlot[data-term-id]").each(function () {
			var termId = this.getAttribute("data-term-id");
			if (!growthWordsByTermId.hasOwnProperty(termId)) { return; }
			var phrase = softNextMarkPhrase(growthWordsByTermId[termId]);
			if (!phrase) { return; }
			var existing = this.getAttribute("data-water-tip");
			this.setAttribute("data-water-tip", existing ? (existing + " " + phrase) : phrase);
		});
	}

	// ── finish screen: the session's bed ──────────────────────────────────────
	// `grew` = [{termId, term, stageBefore, stageAfter, intervalDays}] from session end;
	// `sessionWords` = [{termId, term, reverse}] — EVERY word the session drilled, from the
	// client's own deck (only it knows the set; `grew` is by construction the grown subset).
	//
	// This used to be a row of bordered before → after cards, then a bed of just the words
	// that grew. It is now a patch of soil with the WHOLE session growing in it, each word
	// at its current stage (design §6): a bed holding only the winners kept telling the user
	// their session was three words long when it was seventeen, and hid the far commoner
	// truth — a word that was watered and holds its stage until it is due. The ones that
	// grew glow (see the bed renderer), so the reward is still unmistakable inside the
	// fuller picture. Per-plant detail lives in the hover; clicking the bed opens the whole
	// garden.
	//
	// Stages for the words that did NOT grow come from the same POST api/garden/term-status
	// every other surface decorates with — one extra round trip on a screen that is already
	// waiting on the session-end response.
	// The most recently painted finish-screen bed (one at a time — the finish screen holds
	// exactly one) plus the "a plant is hovered" callback the word list registers once. This
	// is the bridge between the "Grew today" word list (main.js, DOM) and the plant canvas
	// (garden_vista.js, no DOM per plant): hoverBedTerm/clearBedHover drive the canvas from a
	// word hover, onBedHover reports a canvas hover back so the word can bold in turn.
	var currentBed = null;
	var bedHoverCb = null;

	// Painted TWICE per finish screen, and the first pass is what makes the screen feel instant.
	//
	//   grew == null  → pass 1. The session-end response has not landed yet; the bed goes up right
	//                   away from the client's own word set, every plant at the stage it stood at
	//                   BEFORE this session was scored and nothing glowing. The heading says so.
	//   grew == array → pass 2 (possibly empty). The server has said what moved; same words in the
	//                   same order, and the layout jitter is seeded by plant INDEX, so every plant
	//                   keeps its spot and only its stage and its glow change.
	//
	// null and [] are deliberately different values here: "not known yet" is not "nothing grew".
	//
	// The status read-back is NOT cached between the passes. The garden advance also waters words
	// it does not promote, and their next-watering dates are exactly what that changes — a reused
	// pass-1 answer would leave a word that was just watered still drawn as thirsty. The second
	// request costs nothing the user can feel: by then they are already looking at a bed.
	var bedGen = 0;

	function renderGrowthBed(grew, mount, sessionWords) {
		var $mount = mount && mount.jquery ? mount : $(mount);
		if (!$mount.length) { return; }
		var firstPass = (grew == null);
		grew = grew || [];
		var words = (sessionWords && sessionWords.length) ? sessionWords : grew;
		if (!words.length) { $mount.empty(); return; }

		var grewById = {};
		for (var i = 0; i < grew.length; i++) { grewById[grew[i].termId] = grew[i]; }

		var termIds = words.map(function (w) { return w.termId; });
		// The two passes are two live requests, so they can land out of order. Last one ASKED wins:
		// a slow pass 1 must never repaint over the growth pass 2 has already shown.
		var gen = ++bedGen;
		$.ajax("api/garden/term-status", {
			type: "POST", data: { termIds: termIds }, dataType: "json", traditional: true,
			success: function (data) {
				if (gen !== bedGen) { return; }
				var byId = {};
				var terms = (data && data.terms) || [];
				for (var j = 0; j < terms.length; j++) { byId[terms[j].termId] = terms[j]; }
				paintBed($mount, words, grewById, byId, grew.length > 0);
				if (window.PL_FINISH_MARK) { PL_FINISH_MARK(firstPass ? "bed1" : "bed2"); }
			},
			// The bed is a reward, not a workflow: if the status call loses, still plant the
			// words that grew rather than showing the user nothing at all. On pass 1 there is
			// nothing to fall back to yet — stay blank and leave the drawing to pass 2.
			error: function () {
				if (gen !== bedGen || firstPass) { return; }
				paintBed($mount, grew, grewById, {}, grew.length > 0);
			}
		});
	}

	function bedPlant(w, g, st) {
		if (g) {
			// The grown word's own news beats the status row: `grew` carries what it grew FROM,
			// and a word watered seconds ago is never thirsty whatever the read-back says.
			var beforeName = g.stageBefore >= 1 ? stageDisplayName(g.stageBefore) : getRes("garden_ready_stage_name");
			return {
				termId: g.termId, word: g.term, kind: g.stageAfter,
				overdue: false, dueDays: g.intervalDays,
				note: strFormat(getRes("garden_grow_from"), beforeName),
				promoted: true, nav: true
			};
		}
		// st.stage == null, not !st.stage: a v2 sprout is planted AT stage 0, which is falsy in
		// JS — the same trap paintSlot and the row badge above already guard against. Reading it
		// as "no plant" told the owner "Not planted yet" about a word planted hours earlier
		// (report 2026-08-25), on the one garden surface that had missed the v2 sprout.
		if (!st || (st.stage == null && !st.ready)) {
			// Drilled but with nothing planted behind it — a first-ever miss writes no plant
			// (GardenService), so the word has no stage to show. It still belongs in the bed:
			// it was part of the session. Shown as the germination shoot with the schedule
			// lines suppressed, because it has no watering date to promise.
			return { termId: w.termId, word: w.term, kind: "germ", unplanted: true, nav: true };
		}
		return {
			termId: w.termId, word: w.term,
			kind: st.stage != null ? st.stage : "germ",
			overdue: !!st.overdue, dueDays: st.dueDays,
			// the parametric wither (witherDegree) reads its degree off these
			overdueDays: st.overdueDays, intervalDays: st.intervalDays, r: st.r,
			ready: st.stage == null, nav: true
		};
	}

	function paintBed($mount, words, grewById, byId, anyGrew) {
		var plants = words.map(function (w) {
			return bedPlant(w, grewById[w.termId], byId[w.termId]);
		});

		var $title = $('<div class="gardenGrowTitle"></div>')
			.html(markIcon("sprout") + '<span class="gardenGrowTitleTextClass"></span>');
		// The heading names what the picture IS. With something glowing in it the bed is about
		// today's growth; with nothing grown it is honestly just this session's words.
		//
		// ⚠️ Targeted by CLASS, not by `find("span")`. The 2026-08-13 glyph trial turned markIcon's
		// <img> into a <span>, so a bare span selector started matching the MARK as well as the
		// label — .text() then overwrote 🌱 with the heading and the finish screen printed
		// "Today's growth Today's growth" (owner report 2026-08-25). Any future mark that is also
		// a span stays harmless with the label named explicitly.
		$title.find(".gardenGrowTitleTextClass").text(getRes(anyGrew ? "garden_grow_title" : "garden_bed_session"));
		// "Open the whole garden" — a native title= until PR 7/7 of the tooltip engine; it is a
		// PL.tip site now, so it renders like every other explanation on this screen.
		var $bed = $('<div class="gardenGrowBed" role="button" tabindex="0"></div>')
			.assignTooltip("garden_grow_open");
		var $wrap = $('<div class="gardenGrowBedWrap"></div>').append($bed);
		var $edu = $('<span class="gardenSrEdu"></span>').text(getRes("garden_sr_edu"));
		// Heading + patch in one box, the schooling line OUTSIDE it: the onboarding tour
		// spotlights .gardenGrowSpotClass on the finish screen, and "watering twice a day
		// doesn't speed growth" is a garden aside, not part of what the tour is pointing at
		// (owner call 2026-08-11 — it was inside the highlight and read as essential).
		var $spot = $('<div class="gardenGrowSpotClass"></div>').append($title).append($wrap);
		$mount.empty().append($spot).append($edu);

		// minW is deliberately small: a three-word bed should LOOK like a three-word bed.
		// The patch grows with the garden (canvas edge ∝ sqrt(n)) — that is the whole point.
		// A plant click opens its rich entry in the DICTIONARY (gardenNavigate — the same trusted
		// drill-in a word on the card itself takes; main.js uncovers the dictionary box under the
		// finish screen and Esc brings the card straight back). Not the rich popup: this bed sits
		// on a plain page, so there is a real dictionary to go to. The popup stays the garden
		// VISTA's, where the page is already under a modal. stopPropagation in the canvas handler
		// keeps a plant click from also triggering $bed's own "open the whole vista" click below;
		// empty soil still opens the vista, same as before.
		currentBed = GARDEN_BED.mount($bed[0], plants, {
			minW: 150, maxW: 560,
			onPlantClick: function (p) {
				GARDEN_BED.hidePeek();
				// …EXCEPT while the guided walk is escorting. The tour keeps this bed lit and
				// pressable across every finish-screen step (onboarding_guide.js bedAnd), and that
				// standing exception was handing the reader a door OUT of the tour: gardenNavigate
				// uncovers the dictionary under the finish screen and opens the rich card there, so
				// the screen the walk was pointing at is simply gone (owner report 2026-09-01, the
				// third time this has been asked for — "NO ALLOWED CLICK ON RICH VIEW"). The press
				// still answers, and answers with the garden: a plant behaves like the soil around
				// it and opens the VISTA, which is a modal the walk stands down for and picks its
				// step back up when it closes.
				if (window.OB_GUIDE && OB_GUIDE.armed()) { open(); return; }
				if (window.gardenNavigate) { window.gardenNavigate(p.word); }
			},
			// Canvas hover → let the finish screen bold the matching "Grew today" word.
			onHover: function (termId) { if (bedHoverCb) { bedHoverCb(termId); } }
		});

		function open() { GARDEN_BED.hidePeek(); GARDEN_BED.openVista(); }
		$bed.on("click", open).on("keydown", function (e) {
			if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
		});
		// The finish screen's bed is where the onboarding tour opens, and it lands on an
		// async pass — this is the only moment anything can say "it is on screen now".
		if (window.OB_TIPS) { OB_TIPS.evaluate(); }
	}

	// ── "How does my garden grow?" — the one explainer panel ──────────────────
	// docs/project/garden-growth-explainer.md. THE single home of garden education: the vista's
	// ? chip and the finish screen's one-time first-planting card both open exactly this, so the
	// answer to "when does my seedling become the glowing flower?" is written in one place and
	// cannot fork. Every number in the copy is the ladder below read out loud.
	//
	// It lives here rather than in garden_vista.js because it is about a PLANT, not about a bed:
	// its whole top half is the sprite drawn eight times, which is this file's business.
	//
	// The waits that EARN each stage, in days — M5Engine.MILESTONES {1,3,7,14,30,60,90} shifted by
	// one rung: a word promotes when its modeled stability crosses the milestone BELOW the stage it
	// is entering, so the gap that buys stage 7 is 60 days (stage 6's milestone) and 90 is never a
	// wait anyone serves. They sum to 115 days ≈ just under four months, which is the "fastest it
	// can be done" the copy promises.
	var GUIDE_WAIT_DAYS = [1, 3, 7, 14, 30, 60];

	// …but the ladder shows the RUNNING TOTAL, not the gaps (owner's call 2026-08-15). "~2 months"
	// under a rung read as "this step takes two months" when the question the picture is answering
	// is "how far off is the flower?" — a gap answers it only if you add six of them up in your
	// head. Cumulative, the last rung says ~4 months and so agrees, out loud, with the sentence
	// directly beneath it.
	//
	// Printed through approxDuration, and NOT humanizeDays — the two parted company later the same
	// day, when the WATERING copy went exact (see humanizeDays). A plant's next watering is a date
	// the app holds it to and prints to the day; a cumulative horizon is not, and "115 days" under
	// the flower would be a number nobody plans around and the sentence below would contradict.

	// One rung: the plant, what that stage is called, and how long the word has been growing by the
	// time it gets there. Ready has no time (nothing has happened yet) and stage 1 is bought by the
	// first practice rather than by waiting, so those two are named rather than measured.
	function guideRung(kind, name, wait) {
		// The SHARED frame, unlike every other single-plant surface in this file. Those crop to each
		// stage's own art so a sprout stays legible at row height; here the whole point is the
		// opposite — eight stages side by side, and a constant frame is exactly what makes the row
		// read as growth rather than as eight equally tall plants. Same argument the growth strip
		// makes for keeping the frame across its before → after pair.
		//
		// Every optional argument of plantSvg is OMITTED rather than passed as a falsy value: the
		// shared frame and no wither ARE its defaults, and this call then means the same thing
		// whatever the tail of that signature is doing this week. It has already earned that once —
		// the parallel branch it was written against dropped the `title` parameter, and this line
		// needed no edit.
		return '<div class="gardenGuideRung">'
			+ plantSvg(kind, "gardenGuidePlantClass")
			+ '<span class="gardenGuideRungName">' + esc(name) + "</span>"
			+ '<span class="gardenGuideRungWait">' + esc(wait) + "</span>"
			+ "</div>";
	}

	function guideLadderHtml() {
		var html = guideRung("germ", getRes("garden_ready_stage_name"), "—") + guideRung(1, stageDisplayName(1), "1st practice");
		var elapsed = 0;
		for (var stage = 2; stage <= 7; stage++) {
			elapsed += GUIDE_WAIT_DAYS[stage - 2];
			html += guideRung(stage, stageDisplayName(stage), approxDuration(elapsed));
		}
		return html;
	}

	// The v2 variant (spec Sec 2; docs/project/garden-srs-implementation-plan.md P6 card: PR #853's
	// tooltips and this explainer "must be rewritten when this lands or they document a lie"). The
	// FIXED PROOF LADDER's own gaps {1,3,7,14,30,60,90}d, cumulative — genuinely different numbers
	// from GUIDE_WAIT_DAYS above (that array is the OLD M5-milestone-shifted-by-one-rung reading,
	// where stage 7 needs only 60 more days; under v2 it needs the full 90, ~205 days total instead
	// of ~115). Sprout(0) replaces "Ready" as the first rung reached BY PRACTICE rather than by
	// waiting — "Ready" (graduated, never drilled) still opens the ladder for a word that has not
	// been touched at all.
	var GUIDE_WAIT_DAYS_V2 = [1, 3, 7, 14, 30, 60, 90];

	function guideLadderHtmlV2() {
		var html = guideRung("germ", getRes("garden_ready_stage_name"), "—")
			+ guideRung(0, stageDisplayName(0), "1st practice");
		var elapsed = 0;
		for (var stage = 1; stage <= 7; stage++) {
			elapsed += GUIDE_WAIT_DAYS_V2[stage - 1];
			html += guideRung(stage, stageDisplayName(stage), approxDuration(elapsed));
		}
		return html;
	}

	// Set once the finish screen learns v2 growth data exists for THIS user (main.js, off the
	// endSession/growth-v2 response — see docs/project's P6 report for why that is a separate
	// request). Session-scoped in memory only: a fresh page load starts back at v1 and re-learns
	// it the next time a v2-flagged session ends, which is correct — the flag itself can change
	// between deploys and this file has no server-pushed signal of its own to read at boot.
	var v2GuideMode = false;
	function setGrowthV2Mode(active) { v2GuideMode = !!active; }
	// The paired getter — main.js reads this to pick exOutcome_*_tooltip's v2 variant (the
	// growth-copy rewrite garden-srs-implementation-plan.md's "what is still open" item 6 asks
	// for) off the SAME signal this file's own openGrowthGuide() already uses below, rather than
	// a second flag main.js would have to track and keep in sync by hand.
	function isGrowthV2Mode() { return v2GuideMode; }

	// A paragraph of the frozen copy. .html() rather than .text(): a few of these keys carry an
	// <em> or a <b> (the two sub-headings), the same way the garden's tooltip strings already carry
	// <p/>. They are app resources, never user text.
	function guidePara(key, cls) {
		return $('<p class="' + (cls || "") + '"></p>').html(getRes(key));
	}

	// Picks the v2-rewritten key when this page already knows growth-v2 is active, the base key
	// otherwise — the same v2GuideMode signal the ladder graphic above already branches on, reused
	// rather than a second check. Only the three keys that actually state the milestone rule
	// (lead/how/closing) have a _v2 sibling; twoThings/hurryHead/hurryBody/forgetHead/forgetBody/
	// ladder_note/firstplant_card were audited and left alone — see resources_en.js's comment on
	// garden_guide_lead_v2 for the numbers and why the rest make no claim that changes under v2.
	function guideKey(base) { return v2GuideMode ? base + "_v2" : base; }

	var guideLayer = null;

	// May open ON TOP of the vista (that is where its chip lives) — PL.layers stacks them, so Esc
	// closes the panel first and lands back on the garden. Idempotent: a second call while it is
	// open does nothing rather than pushing a second layer over the first.
	function openGrowthGuide() {
		if (guideLayer) { return; }
		var $overlay = $('<div class="gardenVistaOverlay gardenGuideOverlay"></div>');
		var $card = $('<div class="gardenVistaCard gardenGuideCard" role="dialog" aria-modal="true"></div>');
		var $head = $('<div class="gardenVistaHead"></div>')
			.append($('<span class="gardenVistaTitle"></span>').text(getRes("garden_guide_title")));
		var $close = $('<button type="button" class="pl-btn-icon-round gardenVistaClose" aria-label="Close">'
			+ '<span class="pl-ic pl-ic-x"></span></button>');
		// The ladder first, prose second: the picture answers the question before a sentence is read.
		// The caption under it is what keeps the cumulative numbers honest: read alone, "~4 months"
		// beneath the flower is a promise, and it is only true counting from the first practice with
		// every watering on time. One line, muted — the prose below says the rest.
		var $body = $('<div class="gardenGuideBody"></div>')
			.append($('<div class="gardenGuideLadder" aria-hidden="true"></div>')
				.html(v2GuideMode ? guideLadderHtmlV2() : guideLadderHtml()))
			// Both fine-print lines live INSIDE the one note block, which owns the divider under
			// the picture — a second block after it would put the divider between them. The Ready
			// line comes first because it is about the leftmost rung, which is where the eye
			// leaves the strip; the counting line is about the numbers under all the others.
			.append($('<div class="gardenGuideLadderNoteClass"></div>')
				.append($("<div></div>").text(getRes("garden_guide_ready_note")))
				.append($("<div></div>").text(getRes("garden_guide_ladder_note"))))
			.append(guidePara(guideKey("garden_guide_lead"), "gardenGuideLeadClass"))
			.append(guidePara(guideKey("garden_guide_how")))
			.append(guidePara("garden_guide_twoThings"))
			.append($('<p class="gardenGuidePointClass"></p>')
				.append($("<b></b>").text(getRes("garden_guide_hurryHead")))
				.append(" ")
				.append($("<span></span>").html(getRes("garden_guide_hurryBody"))))
			.append($('<p class="gardenGuidePointClass"></p>')
				.append($("<b></b>").text(getRes("garden_guide_forgetHead")))
				.append(" ")
				.append($("<span></span>").html(getRes("garden_guide_forgetBody"))))
			.append(guidePara(guideKey("garden_guide_closing"), "gardenGuideClosingClass"));

		$card.append($head.append($close)).append($body);
		$overlay.append($card).appendTo(document.body);

		guideLayer = PL.layers.push({
			el: $card[0],
			onClose: function () {
				$overlay.remove();
				guideLayer = null;
			}
		});
		$close.on("click", function () { if (guideLayer) { guideLayer.close("api"); } });
	}

	$(loadSprite);   // inject the sprite once the DOM is ready

	// Repaint EVERY stage slot currently on the page, in one batched call. Growing a plant is
	// the one event that invalidates every surface at once — the word may be visible in a
	// word list, a tray and a lean row simultaneously — and the user should never have to
	// close and reopen a list to see that their word grew. Called on session end.
	function refreshAll() {
		decorate(document.body);
	}

	return {
		slot: slot,
		markIcon: markIcon,
		decorate: decorate,
		// The day badge on its own, for surfaces that want the watering countdown without a plant.
		decorateDays: decorateDays,
		refreshAll: refreshAll,
		plantSvg: plantSvg,
		dropletSvg: dropletSvg,
		witherDegree: witherDegree,
		witherRender: witherRender,
		renderGrowthBed: renderGrowthBed,
		// "How does my garden grow?" — one panel, opened by the vista chip and the finish card.
		openGrowthGuide: openGrowthGuide,
		// Word list ↔ growth-bed hover linkage (see the note by `currentBed` above).
		onBedHover: function (fn) { bedHoverCb = fn; },
		hoverBedTerm: function (termId) { if (currentBed) { currentBed.setHoverTerm(termId); } },
		clearBedHover: function () { if (currentBed) { currentBed.clearHoverTerm(); } },
		humanizeDays: humanizeDays,
		approxDuration: approxDuration,
		shortDays: shortDays,
		stageName: stageDisplayName,
		// Garden growth engine v2 (docs/project/garden-srs-redesign-spec.md Sec 2; P6) — all four
		// are no-ops / return nothing (isGrowthV2Mode: false) whenever the finish screen never
		// learns v2 data exists, so a caller never needs to guard a call to them.
		setGrowthV2Mode: setGrowthV2Mode,
		isGrowthV2Mode: isGrowthV2Mode,
		softNextMarkPhrase: softNextMarkPhrase,
		appendGrowthSoftWords: appendGrowthSoftWords,
		// The raw sprite markup, for consumers that must rasterize it into a standalone SVG
		// document (the bed's canvas stamps) rather than <use> it in this one.
		spriteDefs: function () {
			var host = document.getElementById(HOST_ID);
			var svg = host && host.querySelector("svg");
			return svg ? svg.innerHTML : null;
		}
	};
})();
